import { AlertCircle, CheckCircle, FileText, Loader2 } from 'lucide-react'
import { EInvoiceObservationNotice } from './einvoice-observation-notice'
import { EInvoiceViewer } from './einvoice-viewer'

import { EInvoiceStuckCard } from './einvoice-stuck-card'
import { isClosable } from './einvoice-pending-actions'
import { EInvoicePendingLinks } from './einvoice-pending-links'
import { EInvoiceResolvedCard } from './einvoice-resolved-card'
import type { ReactNode } from 'react'
import type {
    EInvoice,
    EInvoiceResolutionSource,
    RefreshEInvoiceDraft,
} from '@/lib/api/einvoice-api'
import { useEInvoiceObservation } from '@/hooks/use-einvoice-observation'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EInvoiceIssuedPanel } from '@/components/sections/einvoice-issued-panel'
import {
    ActionButton,
    RefreshAction,
} from '@/components/sections/einvoice-panel-actions'
import { getEInvoiceFailureMessage } from '@/lib/einvoice-failure-message'
import { orderHandedOver } from '@/lib/einvoice-handover'
import { m } from '@/lib/paraglide/messages'
import { useEInvoiceCredential } from '@/hooks/use-einvoice-credential'
import { MisaCredentialNotice } from '@/components/sections/einvoice-credential-notice'

interface Props {
    orderId: string
    invoice: EInvoice | null
    canMutate: boolean
    pushing: boolean
    refreshing: boolean
    onPush: () => void
    onRefresh: () => void
    /** Same refresh, resolving to the refreshed invoice. */
    onRefreshNow?: () => Promise<EInvoice | undefined>
    onPublish?: (token: string, notIssuedByHand?: boolean) => Promise<unknown>
    onRefreshDraft?: RefreshEInvoiceDraft
    initiallyOpen?: boolean
    /** Clears `initiallyOpen` once a viewer has opened from it. */
    onInitiallyOpened?: () => void
    orderCode?: string
    orderStatus?: string
    /** Đơn "Bán ngay": chốt đơn là được phát hành (M-132). */
    isQuickSale?: boolean
    deliveredAt?: string
    source?: EInvoiceResolutionSource
}

const canPublishFrom = (status: EInvoice['status']) =>
    [
        'draft',
        'pushed',
        'publish_failed',
        'rejected',
        'publish_unknown',
    ].includes(status)

const isResolved = (invoice: EInvoice | null) =>
    invoice?.status === 'manual_issued' || invoice?.status === 'skipped'

export function EInvoicePanelState(props: Props) {
    const observation = useEInvoiceObservation(props.orderId, props.invoice)
    const credential = useEInvoiceCredential()
    const credentialLoading = credential.isLoading || credential.isPending
    const credentialUsable = Boolean(
        credential.data?.configured && !credential.data.invalid_since,
    )
    const canMutate =
        props.canMutate &&
        !credential.isImpersonating &&
        !credentialLoading &&
        !credential.isError &&
        credentialUsable
    const gatedProps = { ...props, canMutate }
    const locked =
        observation && !['draft', 'failed'].includes(observation.publication)
    // Closing by hand needs no MISA account (unless an old draft is removed), so
    // it follows permission only; a read-only impersonation never gets it.
    const canResolve = props.canMutate && !credential.isImpersonating
    const resolved = isResolved(props.invoice)
    const source = props.source ?? 'order_detail'
    const handedOver = orderHandedOver(props.orderStatus, props.isQuickSale)
    // An unanswered publication has no closing buttons yet, so it is not
    // "closable"; it still gets the one card, with its own sentence.
    const unanswered = Boolean(
        canResolve &&
        !resolved &&
        handedOver &&
        props.invoice?.status === 'publish_unknown',
    )
    const pending =
        canResolve &&
        !unanswered &&
        !locked &&
        !resolved &&
        handedOver &&
        isClosable(props.invoice?.status)
    const pendingLinks = (invoice: EInvoice, extra?: ReactNode) => (
        <EInvoicePendingLinks
            extra={extra}
            orderId={props.orderId}
            status={invoice.status}
            orderCode={props.orderCode}
            orderStatus={props.orderStatus}
            isQuickSale={props.isQuickSale}
            canCheck={canMutate}
            checking={props.refreshing}
            onCheck={props.onRefresh}
        />
    )
    return (
        <div className="space-y-2">
            {props.invoice && resolved ? (
                <EInvoiceResolvedCard
                    orderId={props.orderId}
                    invoice={props.invoice}
                    canReopen={canResolve}
                    source={source}
                />
            ) : unanswered || pending ? (
                <EInvoiceStuckCard
                    orderId={props.orderId}
                    invoice={props.invoice}
                    deliveredAt={props.deliveredAt}
                    source={source}
                    canMutate={canMutate}
                    refreshNow={props.onRefreshNow}
                    links={(extra) =>
                        props.invoice
                            ? pendingLinks(props.invoice, extra)
                            : null
                    }
                    publishAction={(label) => (
                        <PendingPrimary
                            {...gatedProps}
                            invoice={props.invoice}
                            label={label}
                        />
                    )}
                />
            ) : observation ? (
                <div className="space-y-2 rounded-md border p-3">
                    <EInvoiceObservationNotice observation={observation} />
                    {canMutate && (
                        <RefreshAction
                            pending={props.refreshing}
                            onClick={props.onRefresh}
                        />
                    )}
                </div>
            ) : (
                <InvoiceState {...gatedProps} />
            )}
            {props.canMutate && !credential.isImpersonating ? (
                credential.isError ? (
                    <div
                        className="space-y-2 rounded-md border p-3 text-sm"
                        role="alert"
                    >
                        <p>{m.einvoice_credential_load_error()}</p>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => void credential.refetch()}
                        >
                            {m.einvoice_credential_retry()}
                        </Button>
                    </div>
                ) : credentialLoading ? (
                    <p role="status" className="text-sm text-muted-foreground">
                        {m.einvoice_credential_loading()}
                    </p>
                ) : !credentialUsable ? (
                    <MisaCredentialNotice
                        invalidSince={credential.data?.invalid_since}
                        invalidReason={credential.data?.invalid_reason}
                    />
                ) : null
            ) : null}
            {!locked &&
                canMutate &&
                props.orderStatus &&
                !handedOver &&
                (!props.invoice ||
                    ['draft', 'pushed', 'failed', 'publish_failed'].includes(
                        props.invoice.status,
                    )) && (
                    <p className="text-sm text-muted-foreground">
                        {m.einvoice_delivery_hint()}
                    </p>
                )}
            {props.invoice && !resolved && !pending && !unanswered && (
                <EInvoiceViewer
                    orderId={props.orderId}
                    status={props.invoice.status}
                    canMutate={canMutate}
                    onPublish={props.onPublish}
                    onRefreshDraft={props.onRefreshDraft}
                    initiallyOpen={props.initiallyOpen}
                    onInitiallyOpened={props.onInitiallyOpened}
                    orderCode={props.orderCode}
                    orderStatus={props.orderStatus}
                    isQuickSale={props.isQuickSale}
                />
            )}
        </div>
    )
}

/** Publish on the pending card: the existing draft-review dialog (which ends in
 *  the publish confirmation), or creating the first draft when there is none. */
function PendingPrimary(props: Props & { label?: string }) {
    const { invoice } = props
    if (!props.canMutate) return null
    if (!invoice)
        return (
            <ActionButton
                primary
                pending={props.pushing}
                icon={FileText}
                onClick={props.onPush}
            >
                {m.einvoice_pending_publish_btn()}
            </ActionButton>
        )
    // A failed row cannot be published: the draft is rebuilt first.
    if (invoice.status === 'failed' && props.onRefreshDraft) {
        const regenerate = props.onRefreshDraft
        return (
            <ActionButton
                pending={props.refreshing}
                icon={FileText}
                onClick={() => void regenerate().catch(() => {})}
            >
                {m.einvoice_pending_regenerate()}
            </ActionButton>
        )
    }
    if (!canPublishFrom(invoice.status)) return null
    return (
        <EInvoiceViewer
            orderId={props.orderId}
            status={invoice.status}
            canMutate
            onPublish={props.onPublish}
            onRefreshDraft={props.onRefreshDraft}
            initiallyOpen={props.initiallyOpen}
            onInitiallyOpened={props.onInitiallyOpened}
            orderCode={props.orderCode}
            orderStatus={props.orderStatus}
            isQuickSale={props.isQuickSale}
            triggerLabel={props.label ?? m.einvoice_pending_publish_btn()}
            republish
        />
    )
}

function InvoiceState(props: Props) {
    const { invoice } = props
    // No row yet: a handed-over order gets the pending card, one not handed
    // over only the delivery hint. Drafts are never created ahead (M-176).
    if (!invoice) return null

    if (invoice.status === 'draft') {
        return (
            <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
                    <FileText className="size-4" />
                    {m.einvoice_draft_title()}
                </div>
                <p className="text-xs text-muted-foreground">
                    {props.canMutate
                        ? m.einvoice_draft_hint()
                        : m.einvoice_viewer_local_draft_hint()}
                </p>
            </div>
        )
    }

    if (invoice.status === 'pushed') {
        return (
            <div className="space-y-2 rounded-md border border-blue-200 bg-blue-50 p-3">
                <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-blue-800">
                    <CheckCircle className="size-4 text-blue-600" />
                    {m.einvoice_pushed_title()}
                    {invoice.stale ? (
                        <Badge variant="destructive">
                            {m.einvoice_stale()}
                        </Badge>
                    ) : null}
                </div>
                <p className="text-xs text-muted-foreground">
                    {props.canMutate
                        ? m.einvoice_pushed_hint()
                        : m.einvoice_viewer_misa_hint()}
                </p>
                {props.canMutate ? (
                    <div className="flex flex-wrap gap-2">
                        <RefreshAction
                            pending={props.refreshing}
                            onClick={props.onRefresh}
                        />
                    </div>
                ) : null}
            </div>
        )
    }

    if (invoice.status === 'rejected') {
        return (
            <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                    <AlertCircle className="size-4" />
                    {m.einvoice_badge_rejected()}
                </div>
                <p className="text-xs text-muted-foreground">
                    {props.canMutate
                        ? m.einvoice_rejected_hint()
                        : m.einvoice_rejected_readonly_hint()}
                </p>
                {props.canMutate ? (
                    <RefreshAction
                        pending={props.refreshing}
                        onClick={props.onRefresh}
                    />
                ) : null}
            </div>
        )
    }

    // MISA đã nói hóa đơn tồn tại nhưng không trả số: không quay spinner như
    // đang chờ, mà chỉ cho kế toán việc cần làm để khỏi phát hành trùng.
    if (invoice.status === 'publish_duplicated') {
        return (
            <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                    <AlertCircle className="size-4" />
                    {m.einvoice_publish_duplicated()}
                </div>
                <p className="text-xs text-muted-foreground">
                    {m.einvoice_publish_duplicated_hint()}
                </p>
                {props.canMutate ? (
                    <RefreshAction
                        pending={props.refreshing}
                        onClick={props.onRefresh}
                    />
                ) : null}
            </div>
        )
    }

    if (invoice.status === 'failed' || invoice.status === 'publish_failed') {
        const failureMessage =
            invoice.status === 'publish_failed'
                ? invoice.error_message
                : getEInvoiceFailureMessage(invoice)
        return (
            <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <div className="flex items-center gap-2 text-sm text-destructive">
                    <AlertCircle className="size-4" />
                    {invoice.status === 'publish_failed'
                        ? m.einvoice_publication_failed()
                        : m.einvoice_failed()}
                </div>
                {failureMessage ? (
                    <p
                        className="text-xs text-muted-foreground"
                        title={failureMessage}
                    >
                        {failureMessage}
                    </p>
                ) : null}
            </div>
        )
    }

    if (invoice.status !== 'issued') {
        const issuing = invoice.status === 'issuing'
        const unknown = ![
            'pending',
            'created',
            'issuing',
            'publishing',
            'publish_unknown',
        ].includes(invoice.status)
        return (
            <div className="space-y-2 rounded-md border p-3">
                <div className="flex items-center gap-2 text-sm">
                    {!unknown && (
                        <Loader2 className="size-4 animate-spin text-primary" />
                    )}
                    {unknown
                        ? m.einvoice_badge_unknown()
                        : invoice.status === 'publish_unknown'
                          ? m.einvoice_publish_unknown()
                          : invoice.status === 'publishing'
                            ? m.einvoice_publishing()
                            : issuing
                              ? m.einvoice_badge_issuing()
                              : m.einvoice_processing()}
                </div>
                {props.canMutate ? (
                    <RefreshAction
                        pending={props.refreshing}
                        onClick={props.onRefresh}
                    />
                ) : null}
            </div>
        )
    }

    return <EInvoiceIssuedPanel invoice={invoice} />
}
